export type BaseCodec = "h264" | "vp8" | "vp9" | "av1" | "hevc";

export type BitrateMode = "constant" | "variable" | "quantizer";

export type HardwareAcceleration =
  | "no-preference"
  | "prefer-hardware"
  | "prefer-software";

export type LatencyMode = "quality" | "realtime";

// WebCodecs Audio Codec Registry: https://w3c.github.io/webcodecs/codec_registry.html#audio-codec-registry

// Using base codec names, profiles are specified in audioProfile

export type AudioCodec =
  | "opus"
  | "aac"
  | "mp3"
  | "flac"
  | "vorbis"
  | "ulaw"
  | "alaw"
  | "pcm";

export interface EncodersConfig {
  // Video

  baseCodec: BaseCodec;

  codecProfile: string;

  framerate: number;

  bitrate: number;

  avcQuantizer: number;

  keyframeIntervalMs: number;

  bitrateMode: BitrateMode;

  hardwareAcceleration: HardwareAcceleration;

  latencyMode: LatencyMode;

  // Audio

  audioCodec: AudioCodec;

  audioProfile: string;

  audioSampleRate: number;

  audioChannels: number;

  audioBitrate: number;
}

export const DEFAULT_ENCODERS_CONFIG: EncodersConfig = {
  // Video

  baseCodec: "h264",

  codecProfile: "avc1.640028",

  framerate: 30,

  bitrate: 6000000,

  avcQuantizer: 35,

  keyframeIntervalMs: 2000,

  bitrateMode: "quantizer",

  hardwareAcceleration: "prefer-hardware",

  latencyMode: "realtime",

  // Audio

  audioCodec: "opus",

  audioProfile: "opus",

  audioSampleRate: 48000,

  audioChannels: 2,

  audioBitrate: 128000,
};

export const DEFAULT_RECORDING_CONFIG: Partial<EncodersConfig> = {
  latencyMode: "quality",

  bitrateMode: "quantizer",

  avcQuantizer: 15,
};

import { openDatabase } from "../../Utils/db";

/**
 * Get the full encoders configuration from user config in DB
 * Returns the default config merged with any saved values from DB
 */

export async function getEncodersConfig(): Promise<EncodersConfig> {
  try {
    const db = await openDatabase();

    const configUsers = await db?.config.toArray();

    const userId = localStorage.getItem("@Eriol/system/userId");

    const userConfigDB = configUsers?.find((item) => item.userId === userId);

    const savedEncoders = userConfigDB?.configuration?.encoders;

    return {
      ...DEFAULT_ENCODERS_CONFIG,

      ...(savedEncoders as Partial<EncodersConfig>),
    };
  } catch {
    return DEFAULT_ENCODERS_CONFIG;
  }
}

/**
 * Get the stored framerate from user config in DB
 * Returns the default (30) if no config is found
 */

export async function getStoredFramerate(): Promise<number> {
  const config = await getEncodersConfig();

  return config.framerate;
}

export interface VideoResolution {
  width: number;

  height: number;

  label: string;
}

/**
 * La resolución de broadcast no se guarda como width/height: vive en el campo de
 * "level" del codecProfile H.264 (avc1.PPCCLL). Mapeamos ese level a la resolución
 * 16:9 canónica. Única fuente de verdad — la usan el resumen del preset (UI) y el
 * arranque del OSR/NVENC (controlBroadcast) para que el broadcast respete el preset.
 */

export function resolutionForProfile(codecProfile: string): VideoResolution {
  const p = (codecProfile || "").toLowerCase();

  // 720p — Baseline/Main 3.1

  if (p.includes("42001f") || p.includes("4d001f")) {
    return { width: 1280, height: 720, label: "720p" };
  }

  // 4K — High 5.1/5.2

  if (p.includes("640033") || p.includes("640034")) {
    return { width: 3840, height: 2160, label: "4K" };
  }

  // 1440p — High/Main 5.0

  if (p.includes("640032") || p.includes("4d0032")) {
    return { width: 2560, height: 1440, label: "1440p" };
  }

  // 1080p — High/Main 4.0/4.2 (default)

  return { width: 1920, height: 1080, label: "1080p" };
}
